import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, useReducedMotion } from 'framer-motion';
import { Box, Button, Chip, Container, Grid, Paper, Stack, Typography } from '@mui/material';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import PlayCircleOutlineRoundedIcon from '@mui/icons-material/PlayCircleOutlineRounded';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import MapRoundedIcon from '@mui/icons-material/MapRounded';
import HubRoundedIcon from '@mui/icons-material/HubRounded';
import QueryStatsRoundedIcon from '@mui/icons-material/QueryStatsRounded';
import AccountTreeRoundedIcon from '@mui/icons-material/AccountTreeRounded';
import DashboardRoundedIcon from '@mui/icons-material/DashboardRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';

import { tArray } from '../lib/i18nSafe';
import { URBANAI_URL, URBANAI_DEMO_VIDEO_URL } from '../lib/seo';
import platformOverview from '../assets/urbanai-platform-overview.webp';

const LIVE_STATUS = new Set(['Live', 'En production', 'مباشر']);

const MODULE_ICONS = {
  reporting: Inventory2RoundedIcon,
  intelligence: MapRoundedIcon,
  matching: HubRoundedIcon,
  predictive: QueryStatsRoundedIcon,
  'knowledge-graph': AccountTreeRoundedIcon,
  dashboards: DashboardRoundedIcon,
  insights: AutoAwesomeRoundedIcon,
  arabic: HubRoundedIcon
};

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } }
};

export default function FlagshipSection() {
  const { t } = useTranslation();
  const reduceMotion = useReducedMotion();

  const capabilities = tArray(t, 'home.flagship.capabilities');
  const relevance = tArray(t, 'home.flagship.relevance');
  const badges = tArray(t, 'home.flagship.badges');

  const Reveal = ({ children, ...props }) => (
    <motion.div
      initial={reduceMotion ? false : 'hidden'}
      whileInView={reduceMotion ? undefined : 'show'}
      viewport={{ once: true, amount: 0.2 }}
      variants={fadeUp}
      {...props}
    >
      {children}
    </motion.div>
  );

  return (
    <Box component="section" sx={{ py: { xs: 7, md: 11 }, borderBottom: '1px solid', borderColor: 'divider' }}>
      <Container maxWidth="xl">
        <Typography
          sx={{
            fontFamily: 'var(--font-mono)',
            fontSize: '0.72rem',
            textTransform: 'uppercase',
            letterSpacing: '0.14em',
            color: 'primary.main',
            mb: 1.5
          }}
        >
          {t('home.flagship.eyebrow')}
        </Typography>
        <Typography
          component="h2"
          sx={{ fontFamily: 'var(--font-serif)', fontSize: { xs: '2.2rem', md: '3.4rem' }, lineHeight: 1.05, letterSpacing: '-0.02em', mb: 2, maxWidth: 900 }}
        >
          {t('home.flagship.title')}
        </Typography>
        <Typography sx={{ color: 'text.secondary', fontSize: '1.1rem', maxWidth: 760, mb: 4 }}>
          {t('home.flagship.description')}
        </Typography>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 5 }}>
          <Button
            component="a"
            href={URBANAI_URL}
            target="_blank"
            rel="noopener noreferrer"
            size="large"
            variant="contained"
            endIcon={<OpenInNewRoundedIcon fontSize="small" />}
            sx={{ bgcolor: 'text.primary', color: 'background.default', borderRadius: 999, '&:hover': { bgcolor: 'text.primary', opacity: 0.9 } }}
          >
            {t('home.flagship.ctaExplore')}
          </Button>
          <Button
            component="a"
            href={URBANAI_DEMO_VIDEO_URL}
            target="_blank"
            rel="noopener noreferrer"
            size="large"
            variant="outlined"
            startIcon={<PlayCircleOutlineRoundedIcon />}
            sx={{ borderRadius: 999, borderColor: 'divider', color: 'text.primary' }}
          >
            {t('home.flagship.ctaDemo')}
          </Button>
          <Button
            component={RouterLink}
            to="/contact?type=pilot"
            size="large"
            variant="outlined"
            endIcon={<ArrowForwardRoundedIcon />}
            sx={{ borderRadius: 999, borderColor: 'divider', color: 'text.primary' }}
          >
            {t('home.flagship.ctaPilot')}
          </Button>
        </Stack>

        {badges.length ? (
          <Stack direction="row" spacing={1} sx={{ mb: 4, flexWrap: 'wrap', gap: 1 }}>
            {badges.map((badge) => (
              <Chip key={badge} label={badge} size="small" variant="outlined" sx={{ borderColor: 'divider', color: 'text.secondary' }} />
            ))}
          </Stack>
        ) : null}

        {/* Screenshot — authentic capture of the live Command Wall */}
        <Reveal>
          <Box sx={{ mb: 6 }}>
            <Box
              component="img"
              src={platformOverview}
              alt={t('home.flagship.screenshotAlt')}
              loading="lazy"
              sx={{ width: '100%', borderRadius: 3, border: '1px solid', borderColor: 'divider', display: 'block' }}
            />
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 1.5, maxWidth: 760 }}>
              {t('home.flagship.screenshotCaption')}
            </Typography>
          </Box>
        </Reveal>

        {/* Capabilities */}
        <Typography sx={{ fontFamily: 'var(--font-serif)', fontSize: '1.7rem', mb: 1, letterSpacing: '-0.01em' }}>
          {t('home.flagship.capabilitiesTitle')}
        </Typography>
        <Typography sx={{ color: 'text.secondary', maxWidth: 720, mb: 3 }}>
          {t('home.flagship.capabilitiesIntro')}
        </Typography>
        <Grid container spacing={2.5} sx={{ mb: 6 }}>
          {Array.isArray(capabilities) &&
            capabilities.map((c, idx) => {
              const Icon = MODULE_ICONS[c.id] || AutoAwesomeRoundedIcon;
              return (
                <Grid key={c.id} item xs={12} sm={6} md={4}>
                  <Reveal transition={{ delay: reduceMotion ? 0 : Math.min(idx, 6) * 0.04 }}>
                    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, height: '100%' }}>
                      <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between" sx={{ mb: 1 }}>
                        <Stack direction="row" spacing={1} alignItems="flex-start">
                          <Icon fontSize="small" sx={{ color: 'primary.main', mt: 0.3 }} />
                          <Typography sx={{ fontFamily: 'var(--font-serif)', fontSize: '1.1rem', lineHeight: 1.25 }}>
                            {c.title}
                          </Typography>
                        </Stack>
                        <Chip
                          label={c.status}
                          size="small"
                          variant="outlined"
                          sx={
                            LIVE_STATUS.has(c.status)
                              ? { borderColor: 'success.main', color: 'success.main', flexShrink: 0 }
                              : { borderColor: 'divider', color: 'text.secondary', flexShrink: 0 }
                          }
                        />
                      </Stack>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {c.body}
                      </Typography>
                    </Paper>
                  </Reveal>
                </Grid>
              );
            })}
        </Grid>

        {/* Relevance */}
        <Typography sx={{ fontFamily: 'var(--font-serif)', fontSize: '1.7rem', mb: 1, letterSpacing: '-0.01em' }}>
          {t('home.flagship.relevanceTitle')}
        </Typography>
        <Typography sx={{ color: 'text.secondary', maxWidth: 720, mb: 3 }}>
          {t('home.flagship.relevanceIntro')}
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {Array.isArray(relevance) &&
            relevance.map((r) => (
              <Grid key={r.title} item xs={12} sm={6} md={2.4}>
                <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, height: '100%' }}>
                  <Typography sx={{ fontWeight: 600, mb: 0.5, fontSize: '0.95rem' }}>{r.title}</Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {r.body}
                  </Typography>
                </Box>
              </Grid>
            ))}
        </Grid>
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', maxWidth: 760 }}>
          {t('home.flagship.disclaimer')}
        </Typography>
      </Container>
    </Box>
  );
}
